import { AvTelemetryEnvelope } from '../models/av-telemetry.model';
import {
  AvTelemetryMetricRange,
  AvTelemetryTickSummary,
} from '../models/av-telemetry-summary.model';

/** Reduce a batch of tick envelopes to min/max per metric, by finding numeric leaves. */
export function summariseTicks(ticks: readonly AvTelemetryEnvelope[]): AvTelemetryTickSummary {
  const metrics = new Map<string, AvTelemetryMetricRange>();
  const groupKeys = new Set<string>();
  let maxGroupFanOut = 0;

  ticks.forEach((tick) => {
    collectNumericLeaves(tick.payload, metrics, groupKeys, (fanOut) => {
      maxGroupFanOut = Math.max(maxGroupFanOut, fanOut);
    });
  });

  return {
    metrics: Object.fromEntries(metrics),
    groupKeys: Array.from(groupKeys),
    maxGroupFanOut: maxGroupFanOut,
    tickCount: ticks.length,
  };
}

function collectNumericLeaves(
  node: unknown,
  metrics: Map<string, AvTelemetryMetricRange>,
  groupKeys: Set<string>,
  onFanOut: (fanOut: number) => void
): void {
  if (!isPlainRecord(node)) {
    return;
  }

  Object.entries(node).forEach(([key, value]) => {
    if (typeof value === 'number') {
      // A non-finite value would poison the metric's range for the whole interval.
      if (Number.isFinite(value)) {
        record(metrics, key, value);
      }
      return;
    }

    if (!isPlainRecord(value)) {
      return;
    }

    // All-record values means a keyed collection: its keys identify streams, not metrics.
    const inner = Object.values(value);
    if (inner.length > 0 && inner.every(isPlainRecord)) {
      const keys = Object.keys(value);
      keys.forEach((groupKey) => groupKeys.add(groupKey));
      onFanOut(keys.length);
    }

    collectNumericLeaves(value, metrics, groupKeys, onFanOut);
  });
}

function record(
  metrics: Map<string, AvTelemetryMetricRange>,
  name: string,
  value: number
): void {
  const existing = metrics.get(name);

  if (!existing) {
    metrics.set(name, { min: value, max: value });
    return;
  }

  existing.min = Math.min(existing.min, value);
  existing.max = Math.max(existing.max, value);
}

/** Arrays are excluded: their indices are not metric names. */
function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
